// @ts-strict-ignore
import { ProjectOptionUtils } from './project-option.utils';
import { ValidatorFn } from '@angular/forms';
import { ProjectOptionDefVO, ProjectOptionVO } from '@project/model/vo/project.vo';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface IIdentifiableOption {
  identifier: string;
}

export enum ProjectOptionType { SimpleText, MultilineText, Selection };
export type SelectionChoice = { value: string, label: string };

export class ProjectOptionWrapper implements IIdentifiableOption {
  public displayName: string;
  public projectOption: ProjectOptionVO;
  public required: boolean = false;
  public optionType: ProjectOptionType;
  public visible: boolean = false;
  public validator: ValidatorFn;
  private _choices: Observable<SelectionChoice[]>;


  get choices(): Observable<SelectionChoice[]> {
    return this._choices;
  }

  set choices(value: Observable<SelectionChoice[]>) {
    this._choices = value.pipe(tap(choices => this.checkValue(choices)));
  }

  public get identifier(): string {
    return this.projectOption.identifier;
  }

  public get value(): string {
    return this.projectOption.value;
  }

  public set value(newValue: string) {
    this.projectOption.value = newValue;
  }

  public get isSimpleText(): boolean {
    return this.optionType === ProjectOptionType.SimpleText;
  }

  public get isMultilineText(): boolean {
    return this.optionType === ProjectOptionType.MultilineText;
  }

  public get isSelection(): boolean {
    return this.optionType === ProjectOptionType.Selection;
  }

  private checkValue(choices: SelectionChoice[]) {
    if (!this.value) {
      return;
    }
    if (!choices.find(choice => choice.value === this.value)) {
      this.optionType = ProjectOptionType.SimpleText;
    }
  }
}

export class ProjectOptionDefWrapper implements IIdentifiableOption {
  public displayName: string;
  public projectOptionDef: ProjectOptionDefVO;

  public get identifier(): string {
    return this.projectOptionDef.identifier;
  }

  public get defaultValue(): string {
    return this.projectOptionDef.defaultValue;
  }

  public get order(): number {
    return this.projectOptionDef.order;
  }

  public get isRequired(): boolean {
    return this.projectOptionDef.isRequired;
  }

  public get isVisible(): boolean {
    return this.projectOptionDef.isVisible;
  }

  public set isVisible(value: boolean) {
    this.projectOptionDef.isVisible = value;
  }
}

export class ProjectOptionContainer<T extends IIdentifiableOption> {
  private _generalOptions: T[] = [];
  private _fsfOptions: T[] = [];
  private _internalOptions: T[] = [];

  public get generalOptions(): T[] {
    return this._generalOptions;
  }

  public get fsfOptions(): T[] {
    return this._fsfOptions;
  }

  public get internalOptions(): T[] {
    return this._internalOptions;
  }

  public addOption(option: T, includeInternalOption: boolean = true): boolean {
    let success: boolean = true;
    if (ProjectOptionUtils.isProjectVersionOption(option.identifier)) {
      this._fsfOptions.push(option);
    } else if (ProjectOptionUtils.isGeneralOption(option.identifier)) {
      this._generalOptions.push(option);
    } else if (ProjectOptionUtils.isFSFOption(option.identifier)) {
      this._fsfOptions.push(option);
    } else if (ProjectOptionUtils.isInternalOption(option.identifier)) {
      if (includeInternalOption) {
        this._internalOptions.push(option);
      } else {
        success = false;
      }
    } else {
      success = false;
    }
    return success;
  }

  public getAll(): T[] {
    let allOptions: T[] = [];
    allOptions = allOptions.concat(this._generalOptions);
    allOptions = allOptions.concat(this._fsfOptions);
    allOptions = allOptions.concat(this._internalOptions);
    return allOptions;
  }
}
